/**
 * Derive Favorites buckets from listening stats + trusted recommendation profiles.
 *
 * A Recording may appear in a Feel bucket only when an accepted profile has a
 * meaningful affinity for that Feel (FAVORITES_FEEL_AFFINITY_MIN). This UI gate
 * does not change recommendation-engine scoring. Ranking: totalQualifiedPlays
 * DESC, then lastQualifiedPlayedAt DESC, then recordingId ASC.
 */

import { FEELS } from '../vibe/definitions';

export const FAVORITES_MAX_PER_FEEL = 3;

/** Minimum feel affinity for Favorites UI bucket membership (not engine scoring). */
export const FAVORITES_FEEL_AFFINITY_MIN = 0.5;

export function feelMembershipFromProfile(profile, {
  minAffinity = FAVORITES_FEEL_AFFINITY_MIN,
} = {}) {
  const affinities = profile?.feelAffinities;
  if (!affinities || typeof affinities !== 'object') return [];
  return Object.keys(affinities).filter((feelId) => {
    const score = affinities[feelId];
    return typeof score === 'number' && Number.isFinite(score) && score >= minAffinity;
  });
}

export function compareListeningStats(a, b) {
  const playsA = Number(a?.totalQualifiedPlays) || 0;
  const playsB = Number(b?.totalQualifiedPlays) || 0;
  if (playsB !== playsA) return playsB - playsA;
  const recencyA = Number(a?.lastQualifiedPlayedAt) || 0;
  const recencyB = Number(b?.lastQualifiedPlayedAt) || 0;
  if (recencyB !== recencyA) return recencyB - recencyA;
  return String(a?.recordingId || '').localeCompare(String(b?.recordingId || ''));
}

export function buildFavoritesByFeel({
  listeningStats = [],
  profilesByRecordingId = {},
  maxPerFeel = FAVORITES_MAX_PER_FEEL,
} = {}) {
  const buckets = {};
  FEELS.forEach((feel) => {
    buckets[feel.id] = [];
  });

  const ranked = [...listeningStats]
    .filter((stat) => stat?.recordingId && (Number(stat.totalQualifiedPlays) || 0) > 0)
    .sort(compareListeningStats);

  ranked.forEach((stat) => {
    const profile = profilesByRecordingId[stat.recordingId];
    if (!profile || profile.acceptanceStatus !== 'accepted') return;
    const feels = feelMembershipFromProfile(profile);
    feels.forEach((feelId) => {
      if (!buckets[feelId]) return;
      if (buckets[feelId].length >= maxPerFeel) return;
      if (buckets[feelId].some((item) => item.recordingId === stat.recordingId)) return;
      buckets[feelId].push({
        recordingId: stat.recordingId,
        title: stat.title || profile.displayTitle || 'Track',
        artist: stat.artist || profile.displayArtist || '',
        totalQualifiedPlays: Number(stat.totalQualifiedPlays) || 0,
        lastQualifiedPlayedAt: Number(stat.lastQualifiedPlayedAt) || null,
        providerAssetKey: profile.providerAssetKey || null,
        artworkUrl: profile.artworkUrl || null,
      });
    });
  });

  return FEELS.map((feel) => ({
    feelId: feel.id,
    label: feel.label,
    items: buckets[feel.id] || [],
  }));
}
